import { BugCatchClient } from './client.js';
import type { BugCatchOptions, UserContext, BreadcrumbEntry, LogLevel } from './types.js';
import type { BugCatchTransaction } from './tracing.js';

export type { BugCatchOptions, UserContext, BreadcrumbEntry };
export type {
  EventPayload,
  StackFrame,
  ParsedDsn,
  LogLevel,
  LogPayload,
  TransactionPayload,
  SpanPayload,
  SessionPayload,
  SessionStatus,
  WebVitalPayload,
  WebVitalMetric,
  ReplayChunkPayload,
} from './types.js';
export { BugCatchTransaction, BugCatchSpan } from './tracing.js';
export { BugCatchClient };
export { BugCatchServerReporter } from './server-reporter.js';
export type { ServerReporterOptions } from './server-reporter.js';

// ─── Singleton instance ───────────────────────────────────────────────────────

let _client: BugCatchClient | null = null;

function getClient(): BugCatchClient {
  if (!_client) {
    throw new Error('[BugCatch] SDK not initialized. Call BugCatch.init(options) first.');
  }
  return _client;
}

// ─── Public singleton API ─────────────────────────────────────────────────────

const BugCatch = {
  /**
   * Initialize the SDK. Call this once, as early as possible in your app.
   *
   * @example
   * BugCatch.init({
   *   dsn: 'http://localhost:3000/ingest/project-id?key=sdk-key',
   *   release: '1.0.0',
   *   environment: 'production',
   * });
   */
  init(options: BugCatchOptions): BugCatchClient {
    if (_client) {
      console.warn('[BugCatch] init() called more than once. Ignoring.');
      return _client;
    }
    _client = new BugCatchClient(options);
    return _client;
  },

  /**
   * Capture an Error object or any value as an error event.
   * Returns the generated event_id.
   */
  captureException(error: unknown, extra?: Record<string, unknown>): string {
    return getClient().captureException(error, extra);
  },

  /**
   * Capture a plain text message as an event.
   * Returns the generated event_id.
   */
  captureMessage(message: string, level?: string, extra?: Record<string, unknown>): string {
    return getClient().captureMessage(message, level, extra);
  },

  /**
   * Set the current user context. Attached to all subsequent events.
   */
  setUser(user: UserContext): void {
    getClient().setUser(user);
  },

  /**
   * Clear the current user context (e.g. on logout).
   */
  clearUser(): void {
    getClient().clearUser();
  },

  /**
   * Set a tag that will be attached to all subsequent events.
   */
  setTag(key: string, value: string): void {
    getClient().setTag(key, value);
  },

  /**
   * Manually add a breadcrumb.
   */
  addBreadcrumb(crumb: BreadcrumbEntry): void {
    getClient().addBreadcrumb(crumb);
  },

  /**
   * Queue a structured log line. Batched and flushed automatically -
   * does not send a request per call.
   *
   * @example
   * BugCatch.captureLog('info', 'Checkout completed', { orderId: '4821' });
   */
  captureLog(
    level: LogLevel,
    message: string,
    attributes?: Record<string, unknown>,
    traceId?: string,
  ): void {
    getClient().captureLog(level, message, attributes, traceId);
  },

  /**
   * Set the trace id attached to every subsequent `captureLog()` call that
   * doesn't pass its own. Global mutable state - a Node server handling
   * concurrent requests should pass `traceId` explicitly to `captureLog()`
   * instead.
   */
  setTraceId(traceId: string): void {
    getClient().setTraceId(traceId);
  },

  /** Clear the trace id set via `setTraceId()`. */
  clearTraceId(): void {
    getClient().clearTraceId();
  },

  /** Send any queued log lines immediately instead of waiting for the next flush. */
  flushLogsNow(): Promise<void> {
    return getClient().flushLogsNow();
  },

  /**
   * Send any queued session updates immediately instead of waiting for the
   * next flush. Useful before a short-lived script exits.
   */
  flushSessionsNow(): Promise<void> {
    return getClient().flushSessionsNow();
  },

  /**
   * Send any queued Web Vitals readings immediately instead of waiting for
   * the next flush. Web Vitals capture is automatic in the browser
   * (`autoCaptureWebVitals: true` by default) - you rarely need this.
   */
  flushWebVitalsNow(): Promise<void> {
    return getClient().flushWebVitalsNow();
  },

  /**
   * Start recording session replay if it isn't already running. Only needed
   * when `sessionReplay: true` wasn't set at `init()` - e.g. starting a
   * recording after a user opts in. No-op outside the browser.
   *
   * @example
   * BugCatch.startReplayNow();
   */
  startReplayNow(): void {
    getClient().startReplayNow();
  },

  /** Stop the active session replay recording (if any) and flush what's queued. */
  stopReplayNow(): void {
    getClient().stopReplayNow();
  },

  /** Send any queued session replay chunks immediately instead of waiting for the next flush. */
  flushReplayNow(): Promise<void> {
    return getClient().flushReplayNow();
  },

  /** The active replay recording's id, if session replay is currently running. */
  getReplayId(): string | undefined {
    return getClient().getReplayId();
  },

  /**
   * Whether the given user (or the currently `setUser()`-set one) may be
   * session-replay recorded under this project's privacy allowlist. Fails
   * closed - a network error counts as "not allowed". `startReplayNow()`
   * and the `sessionReplay: true` init option already check this before
   * recording starts; call it directly if your app wants to decide first
   * (e.g. to show/hide a "recording" indicator) rather than find out only
   * after attempting to start.
   *
   * @example
   * if (await BugCatch.isReplayAllowed()) BugCatch.startReplayNow();
   */
  isReplayAllowed(userId?: string): Promise<boolean> {
    return getClient().isReplayAllowed(userId);
  },

  /**
   * Start a new traced transaction. Queue for delivery by calling
   * `.finish()` on it (and on any spans you started).
   *
   * @example
   * const txn = BugCatch.startTransaction('GET /api/orders/:id', 'http.server');
   * const span = txn.startChild('db.query', 'SELECT * FROM orders WHERE id = ?');
   * span.finish();
   * txn.setHttpStatus(200);
   * txn.finish();
   */
  startTransaction(name: string, op?: string): BugCatchTransaction {
    return getClient().startTransaction(name, op);
  },

  /**
   * Set the transaction that fetch/XHR auto-instrumentation attaches child
   * spans to. Global mutable state - a Node server handling concurrent
   * requests should use `req.bugcatchTransaction` (set by
   * `tracingMiddleware()`) instead of relying on this.
   */
  setCurrentTransaction(transaction: BugCatchTransaction | undefined): void {
    getClient().setCurrentTransaction(transaction);
  },

  /** The transaction set via `setCurrentTransaction`, if any. */
  getCurrentTransaction(): BugCatchTransaction | undefined {
    return getClient().getCurrentTransaction();
  },

  /** Send any queued transactions immediately instead of waiting for the next flush. */
  flushTransactionsNow(): Promise<void> {
    return getClient().flushTransactionsNow();
  },

  /**
   * Express/Connect-style middleware: starts an `http.server` transaction
   * per request, finishes it when the response ends.
   * Drops 404s by default, and any route you name via `ignoreRoutes` (e.g.
   * health checks) - see `BugCatchClient.tracingMiddleware()`.
   *
   * @example
   * app.use(BugCatch.tracingMiddleware());
   * app.use(BugCatch.tracingMiddleware({ ignoreRoutes: ['/health'] }));
   */
  tracingMiddleware(options?: { ignoreStatusCodes?: number[]; ignoreRoutes?: Array<string | RegExp> }) {
    return getClient().tracingMiddleware(options);
  },

  /**
   * Auto-instrument TypeORM: every query run through the given `DataSource`
   * becomes a `db.query` child span on the current transaction.
   *
   * @example
   * BugCatch.instrumentTypeOrm(dataSource); // the TypeORM DataSource
   */
  instrumentTypeOrm(
    dataSource: Parameters<BugCatchClient['instrumentTypeOrm']>[0],
  ): () => void {
    return getClient().instrumentTypeOrm(dataSource);
  },

  /**
   * Auto-instrument ioredis: every command sent through the given `Redis`
   * class becomes a `cache.command` child span on the current transaction.
   *
   * @example
   * import Redis from 'ioredis';
   * BugCatch.instrumentIoredis(Redis);
   */
  instrumentIoredis(
    RedisClass: Parameters<BugCatchClient['instrumentIoredis']>[0],
  ): () => void {
    return getClient().instrumentIoredis(RedisClass);
  },

  /**
   * Manually report an API call timing to BugCatch.
   * Use when `autoTrackRequests` is off or for server-side tracking.
   *
   * @example
   * const start = Date.now();
   * await fetch('/api/orders');
   * BugCatch.trackRequest('GET', '/api/orders', Date.now() - start, 200);
   */
  trackRequest(method: string, route: string, durationMs: number, statusCode: number): void {
    getClient().trackRequest(method, route, durationMs, statusCode);
  },

  /**
   * Tear down all listeners. Useful in SPA cleanup or hot-reload scenarios.
   */
  destroy(): void {
    _client?.destroy();
    _client = null;
  },
};

// Named export for CJS consumers: const { BugCatch } = require('bugcatch-sdk')
export { BugCatch };

// Default export for ESM consumers: import BugCatch from 'bugcatch-sdk'
export default BugCatch;
